import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { Box, Dialog, DialogTitle } from '@mui/material'
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridValueFormatterParams,
  GridValueGetterParams
} from '@mui/x-data-grid-pro'
import dayjs, { Dayjs } from 'dayjs'
import { useContext, useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useGetTimeClockWithRelated } from '../../hooks/useGetTimeClockWithRelated/useGetTimeClockWithRelated'
import { AuthContext } from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { useListTimeClockEntriesQuery } from '../../redux/api/timeClockApi'
import {
  EmployeeRole,
  GetUsersWithPermissionActEnum,
  TimeClockEntry
} from '../../sdk'
import { describeVehicleYMM } from '../../utils'
import { describeCustomer } from '../../utils/describeCustomer/describeCustomer'
import { formatTimeDuration } from '../../utils/formatTimeDuration/formatTimeDuration'
import { DeleteTimeClockDialog } from './DeleteTimeClockDialog'
import { UpdateTimeClockDialog } from './UpdateTimeClockDialog'

export const TimeClockTable = ({
  employee,
  date
}: {
  employee: EmployeeRole
  date: Dayjs | null
}) => {
  const { currentUser } = useContext(AuthContext)
  const [deleteEntry, setDeleteEntry] = useState<TimeClockEntry>()
  const [updateEntry, setUpdateEntry] = useState<TimeClockEntry>()
  const { gridBoxHeight } = useContext(AppContext)
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data: openTimeClocks, isLoading: isLoadingOpen } =
    useListTimeClockEntriesQuery({
      authorization: accessToken,
      shop: shopId,
      employee: [employee.userProfile!.id!],
      isOpenOnly: true
    })
  const { data: recentTimeClocks, isLoading } = useListTimeClockEntriesQuery({
    authorization: accessToken,
    shop: shopId,
    employee: [employee.userProfile!.id!],
    isClosedOnly: true,
    startDateInclusive: (date && date.toISOString()) || undefined,
    endDateExclusive: (date && date.add(1, 'day').toISOString()) || undefined,
    numResults: 150
  })
  const timeClocks = useMemo(
    () => [...(openTimeClocks || []), ...(recentTimeClocks || [])],
    [openTimeClocks, recentTimeClocks]
  )
  const timeClocksWithRelated = useGetTimeClockWithRelated(timeClocks)

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'employeeName',
        headerName: 'Name',
        flex: 2
      },
      {
        field: 'status',
        headerName: 'Productivity Status',
        flex: 3,
        renderCell: (params: GridRenderCellParams<any, any, boolean>) => (
          <>
            {params.row.isOpen ? (
              <FiberManualRecordIcon color="success" />
            ) : (
              <></>
            )}{' '}
            {params.value}{' '}
          </>
        )
      },
      {
        field: 'repairOrder',
        headerName: 'RO #',
        flex: 1
      },
      {
        field: 'vehicle',
        headerName: 'Vehicle',
        flex: 3
      },
      {
        field: 'customer',
        headerName: 'Customer',
        flex: 2
      },
      {
        field: 'clockInDateTime',
        headerName: 'Start',
        flex: 4,
        type: 'dateTime',
        valueFormatter: (params: GridValueFormatterParams<Date>) => {
          if (params.value) {
            return dayjs(params.value).format('h:mm A')
          }
          return ''
        }
      },
      {
        field: 'clockOutDateTime',
        headerName: 'End',
        flex: 4,
        type: 'dateTime',
        valueFormatter: (params: GridValueFormatterParams<Date>) => {
          if (params.value) {
            return dayjs(params.value).format('h:mm A')
          }
          return ''
        }
      },
      {
        field: 'hours',
        headerName: 'Hours',
        flex: 2,
        valueGetter: (params: GridValueGetterParams) => {
          if (params.row.clockOutDateTime) {
            return formatTimeDuration(
              params.row.clockOutDateTime,
              params.row.clockInDateTime
            )
          } else {
            return ''
          }
        }
      },
      {
        field: 'actions',
        type: 'actions',
        flex: 3,
        getActions: (params: GridRenderCellParams) => {
          const actions = []
          if (
            currentUser?.permissions.has(
              GetUsersWithPermissionActEnum.ManageTimeClock
            )
          ) {
            actions.push(
              <GridActionsCellItem
                key="Edit"
                icon={<EditIcon />}
                label="Edit"
                onClick={() => {
                  setUpdateEntry(timeClocks?.find((t) => t.id === params.id!))
                }}
              />
            )
          }
          actions.push(
            <GridActionsCellItem
              key="Delete"
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                setDeleteEntry(timeClocks?.find((t) => t.id === params.id!))
              }}
            />
          )
          return actions
        }
      }
    ],
    [currentUser?.permissions, timeClocks]
  )
  const rows: GridRowsProp = useMemo(() => {
    return (
      timeClocksWithRelated?.map((t) => ({
        ...t,
        ...t.info!,
        employeeName: employee!.userProfile!.name!,
        status: t.relatedStatus?.info!.title || 'No Status',
        isOpen: !t.info!.clockOutDateTime,
        repairOrder: t?.relatedRepairOrder
          ? t.relatedRepairOrder.repairOrder!.localId
          : '',
        vehicle: t?.relatedRepairOrder
          ? describeVehicleYMM(t.relatedRepairOrder.vehicle!.info!)
          : '',
        customer: t?.relatedRepairOrder
          ? describeCustomer(t.relatedRepairOrder.customer!)
          : ''
      })) || []
    )
  }, [employee, timeClocksWithRelated])

  return (
    <Box sx={{ height: gridBoxHeight }}>
      {deleteEntry && (
        <Dialog open={true} onClose={() => setDeleteEntry(undefined)}>
          <DialogTitle>Delete Time Clock</DialogTitle>

          <DeleteTimeClockDialog
            onClose={() => setDeleteEntry(undefined)}
            entry={deleteEntry}
          />
        </Dialog>
      )}
      {updateEntry && (
        <Dialog open={true} onClose={() => setDeleteEntry(undefined)}>
          <DialogTitle>Update Time Clock</DialogTitle>
          <UpdateTimeClockDialog
            onClose={() => setUpdateEntry(undefined)}
            entry={updateEntry}
            timeClocks={timeClocks}
          />
        </Dialog>
      )}
      <DataGridPro
        disableRowSelectionOnClick
        loading={isLoading || isLoadingOpen}
        density="compact"
        columns={columns}
        rows={rows}
        sx={{ border: '0px' }}
      />
    </Box>
  )
}
